interface TrialPlot {
  plot?: {
    id?: number;
    isStripPlot?: boolean;
    column_size?: number;
    plotWidth?: number;
    row_size?: number;
    plotHeight?: number;
    stripArea?: number;
    stripLength?: number;
    stripWidth?: number;
  };
}

interface StripsData {
  stripDetails?: Array<{
    area?: number;
    stripLength?: number;
    stripWidth?: number;
  }>;
}

/**
 * Convert area from square meters to hectares
 */
const convertM2ToHa = (areaM2: number): number => {
  return areaM2 / 10000;
};

/**
 * Calculate strip area in hectares
 */
const calculateStripArea = (
  colIndex: number,
  trialPlot: TrialPlot,
  stripsData?: StripsData
): number => {
  const stripDetail = stripsData?.stripDetails?.[colIndex];

  // Method 1: Get from stripDetails
  if (stripDetail?.area) {
    return convertM2ToHa(stripDetail.area);
  }

  // Method 2: Get from plot properties
  if (trialPlot.plot?.stripArea) {
    return convertM2ToHa(trialPlot.plot.stripArea);
  }

  // Method 3: Calculate from length × width
  const stripLength =
    stripDetail?.stripLength ?? trialPlot.plot?.stripLength ?? 0;
  const stripWidth = stripDetail?.stripWidth ?? trialPlot.plot?.stripWidth ?? 0;

  if (stripLength > 0 && stripWidth > 0) {
    return convertM2ToHa(stripLength * stripWidth);
  }

  return 0;
};

/**
 * Calculate regular plot area in hectares
 */
const calculateRegularPlotArea = (trialPlot: TrialPlot): number => {
  const plotWidth =
    trialPlot.plot?.column_size ?? trialPlot.plot?.plotWidth ?? 0;
  const plotHeight =
    trialPlot.plot?.row_size ?? trialPlot.plot?.plotHeight ?? 0;

  if (plotWidth > 0 && plotHeight > 0) {
    return convertM2ToHa(plotWidth * plotHeight);
  }

  return 0;
};

/**
 * Parse plot ID and find corresponding trial plot
 */
const findTrialPlot = (
  plotId: string | number,
  trialPlots: TrialPlot[],
  plotIndex?: number
): { trialPlot: TrialPlot | undefined; colIndex: number } => {
  const plotIdStr = String(plotId);
  const parts = plotIdStr.split("-");
  let colIndex = 0;

  if (parts.length >= 3) {
    const plotGroupId = Number.parseInt(parts[0], 10);
    colIndex = Number.parseInt(parts[2], 10);
    const trialPlot = trialPlots.find((tp) => tp.plot?.id === plotGroupId);

    // If plot not found by ID but plotIndex is provided, use it as fallback
    if (!trialPlot && typeof plotIndex === "number" && plotIndex >= 0) {
      return { trialPlot: trialPlots[plotIndex], colIndex };
    }

    return { trialPlot, colIndex };
  }

  if (parts.length === 1) {
    colIndex = Number.parseInt(parts[0], 10) - 1;
  }

  const trialPlot =
    typeof plotIndex === "number" && plotIndex >= 0
      ? trialPlots[plotIndex]
      : undefined;

  return { trialPlot, colIndex };
};

/**
 * Calculate plot or strip area in hectares
 * @param plotId - The plot_id string (format: "{id}-{row}-{col}")
 * @param trialPlots - Array of trial plots
 * @param stripsData - Strips data (if in strip mode)
 * @param plotIndex - The index of the plot group in trial_plots array
 * @returns Area in hectares, or 0 if cannot be calculated
 */
export const calculatePlotAreaHa = (
  plotId: string | number | undefined,
  trialPlots: TrialPlot[],
  stripsData?: StripsData,
  plotIndex?: number
): number => {
  if (!plotId || !trialPlots || trialPlots.length === 0) return 0;

  const { trialPlot, colIndex } = findTrialPlot(plotId, trialPlots, plotIndex);

  if (!trialPlot) return 0;

  if (trialPlot.plot?.isStripPlot) {
    return calculateStripArea(colIndex, trialPlot, stripsData);
  }

  return calculateRegularPlotArea(trialPlot);
};

/**
 * Format area from square meters to hectares with proper display
 * @param areaInSquareMeters - Area in square meters (can be null/undefined)
 * @returns Formatted string with area in hectares (e.g., "12.34 ha") or "-" if invalid
 */
export const formatAreaInHectares = (
  areaInSquareMeters?: number | null
): string => {
  if (areaInSquareMeters === undefined || areaInSquareMeters === null) {
    return "-";
  }
  const hectares = areaInSquareMeters / 10000;
  return `${hectares.toFixed(2)} ha`;
};

/**
 * Calculate total seeds or kg based on rate and area
 * @param rate - The rate per unit (seeds/ha, seeds/m², or kg/ha)
 * @param areaHa - The area in hectares
 * @param unit - The unit of rate (seeds/ha, seeds/m², kg/ha)
 * @returns Total amount
 */
export const calculateTotalAmount = (
  rate: number,
  areaHa: number,
  unit: string
): number => {
  if (rate == null || !areaHa) return 0;

  // For seeds/m² or kg/m², convert ha to m²
  if (unit.includes("/m2") || unit.includes("/m²")) {
    const areaM2 = areaHa * 10000;
    return rate * areaM2;
  }

  // For seeds/ha or kg/ha, use ha directly
  return rate * areaHa;
};
